import type { BookingDraft, CheckoutPreference, Services, StepsTabs } from '@/scopes/checkout/domain/types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { produce } from 'immer'
import { useSearchParams } from 'next/navigation'
import { useRouter } from 'next/router'
import { compose, filter, map, not, pipe, prop, uniqBy, values } from 'ramda'

import * as React from 'react'
import { useCMSPageField } from '@/cms/fields'
import { useBookingNumber } from '@/domain/groups/hooks'
import { useSendSelectItem } from '@/scopes/checkout/domain/analytics/useSendSelectItem'
import { ServiceType } from '@/scopes/checkout/domain/types'
import { serviceMappers } from '@/scopes/checkout/infrastructure/api/serviceMappers'
import { formatDateToYYYMMDD } from '@/scopes/common/components/Filters/Participants/dateHelpers'
import { useCookieConsent } from '@/scopes/common/infrastructure/cookie-consent/useCookieConsent'
import { useCouponStore } from '../components/CouponCode/store'
import { useEngineErrorContactInfoStore } from '../components/EngineError/store'
import { checkoutAPIClient } from '../infrastructure/api'
import { useCheckoutContext } from './CheckoutContext'
import { RyanairSessionData } from './types/ryanairSessionData'

function updateServiceInBookingDraft<K extends ServiceType>(serviceType: K, service: Services[K]) {
  return produce((currentBookingDraft: BookingDraft) => {
    currentBookingDraft.services[serviceType] = service
  })
}

function updateGenericServiceInBookingDraft<K extends ServiceType>(serviceType: K, service: Services[K]) {
  return produce((currentBookingDraft: BookingDraft) => {
    currentBookingDraft.services[serviceType] = {
      ...(currentBookingDraft.services[serviceType] || {}),
      [service.id]: service,
    }
  })
}

function updatePreferenceInBookingDraft(code: string, preference: CheckoutPreference) {
  return produce((currentBookingDraft: BookingDraft) => {
    if (!currentBookingDraft.preferences)
      currentBookingDraft.preferences = {}
    currentBookingDraft.preferences[code] = preference
  })
}

export function useService<K extends ServiceType>(serviceType: K) {
  const queryClient = useQueryClient()
  const sid = useCheckoutSid()
  const { contextItemId } = useCheckoutContext()
  const queryKey = useBookingDraftQueryKey()
  const debug = useCheckoutDebugParameter()
  const testdata = useCheckoutTestDataParameter()

  const { isLoading, isFetching, data } = useBookingDraftQuery()

  const { sendSelectItemEvent } = useSendSelectItem({
    serviceMappers,
  })

  const service = data?.services[serviceType] ?? null

  const setServiceMutation = useMutation({
    mutationFn: async (updatedService: Services[K]) => {
      await checkoutAPIClient.setServiceSelection({ sid, contextItemId, debug, testdata }, serviceType, updatedService)
    },
    onMutate: async (updatedService: Services[K]) => {
      await queryClient.cancelQueries({ queryKey })
      const previousBookingDraft = queryClient.getQueryData(queryKey)

      queryClient.setQueryData(queryKey, updateServiceInBookingDraft(serviceType, updatedService))

      return { previousBookingDraft }
    },
    onError: async (_, __, context) => {
      if (!context) {
        return
      }
      queryClient.setQueryData(queryKey, context.previousBookingDraft)
      await queryClient.invalidateQueries({ queryKey })
    },
    onSuccess: async (_, __, context) => {
      if (!service) {
        return
      }
      await queryClient.invalidateQueries({ queryKey })

      const newBookingDraft = queryClient.getQueryData(queryKey)
      if (newBookingDraft) {
        const serviceUpdated = (newBookingDraft as BookingDraft).services[serviceType]
        sendSelectItemEvent(serviceUpdated, serviceType, context?.previousBookingDraft as BookingDraft)
      }
    },
  })

  const setServiceLocalValue = React.useCallback(
    (serviceUpdateFn: (service: Services[K]) => Services[K]) => {
      if (!service)
        return
      const updatedService = serviceUpdateFn(service)
      queryClient.setQueryData(queryKey, updateServiceInBookingDraft(serviceType, updatedService))
    },
    [service, queryClient, queryKey, serviceType],
  )

  return {
    isLoading,
    isFetching,
    service,
    setServiceLocalValue,
    setServiceValue: setServiceMutation.mutate,
  }
}

export function useGenericService<K extends ServiceType>(serviceType: K, type: string) {
  const queryClient = useQueryClient()
  const sid = useCheckoutSid()
  const { contextItemId } = useCheckoutContext()
  const queryKey = useBookingDraftQueryKey()
  const debug = useCheckoutDebugParameter()
  const testdata = useCheckoutTestDataParameter()
  const { isLoading, isFetching, data } = useBookingDraftQuery()

  const { sendSelectItemGenericServiceEvent } = useSendSelectItem({
    serviceMappers,
  })

  const genericTypeService = data?.services[serviceType] ?? null
  const service
    = genericTypeService && type ? (genericTypeService as unknown as Record<string, Services[K]>)[type] : null

  const setServiceMutation = useMutation({
    mutationFn: async (updatedService: Services[K]) => {
      await checkoutAPIClient.setServiceSelection({ sid, contextItemId, debug, testdata }, serviceType, updatedService)
    },
    onMutate: async (updatedService: Services[K]) => {
      await queryClient.cancelQueries({ queryKey })
      const previousBookingDraft = queryClient.getQueryData(queryKey)

      queryClient.setQueryData(queryKey, updateGenericServiceInBookingDraft(serviceType, updatedService))

      return { previousBookingDraft }
    },
    onError: async (_, __, context) => {
      if (!context) {
        return
      }
      queryClient.setQueryData(queryKey, context.previousBookingDraft)
      await queryClient.invalidateQueries({ queryKey })
    },
    onSuccess: async (_, __, context) => {
      if (!service) {
        return
      }
      await queryClient.invalidateQueries({ queryKey })
      const newBookingDraft = queryClient.getQueryData(queryKey)
      if (newBookingDraft) {
        const genericService = (newBookingDraft as BookingDraft).services[serviceType]
        const serviceUpdated = (genericService as unknown as Record<string, Services[K]>)[service.id] ?? null
        sendSelectItemGenericServiceEvent(
          serviceUpdated,
          serviceType,
          context?.previousBookingDraft as BookingDraft,
        )
      }
    },
  })

  const setServiceLocalValue = React.useCallback(
    (serviceUpdateFn: (service: Services[K]) => Services[K]) => {
      if (!service)
        return
      const updatedService = serviceUpdateFn(service)
      queryClient.setQueryData(queryKey, updateGenericServiceInBookingDraft(serviceType, updatedService))
    },
    [service, queryClient, queryKey, serviceType],
  )

  return {
    isLoading,
    isFetching,
    service,
    setServiceLocalValue,
    setServiceValue: setServiceMutation.mutate,
  }
}

export function useCheckoutMessage(componentId: string) {
  const { isLoading, isFetching, data } = useBookingDraftQuery()

  return {
    isLoading,
    isFetching,
    message: data?.messages[componentId] ?? null,
  }
}

export function useCheckoutUsps(componentId: string) {
  const { isLoading, isFetching, data } = useBookingDraftQuery()

  return {
    isLoading,
    isFetching,
    data: data?.usps ? data?.usps[componentId] : null,
  }
}

export function usePriceOverviews(componentId?: string) {
  const { isLoading, isFetching, data } = useBookingDraftQuery()

  return {
    isLoading,
    isFetching,

    data: data?.priceOverviews
      ? Object.entries(data?.priceOverviews ?? []).find(([key]) => componentId?.includes(key))?.[1]
      : null,
  }
}

export function usePreferences() {
  const { isLoading, isFetching, data } = useBookingDraftQuery()
  const queryClient = useQueryClient()
  const queryKey = useBookingDraftQueryKey()
  const preferences = data?.preferences ?? null

  const setPreferencesSelectionValue = React.useCallback(
    (preferenceUpdateFn: (preference: CheckoutPreference) => CheckoutPreference, code: string) => {
      if (!preferences || !code)
        return
      const updatedPreference = preferenceUpdateFn(preferences[code])
      queryClient.setQueryData(
        queryKey,
        updatePreferenceInBookingDraft(code, updatedPreference),
      )
    },
    [preferences, queryClient, queryKey],
  )

  return {
    isLoading,
    isFetching,
    setPreferencesSelectionValue,
    preferences: data?.preferences ?? null,
  }
}

const uniqServicesById = uniqBy(prop('id'))
const filterUnselectedServices = filter(compose(not, prop('isSelected')))
const getUnselectedServiceTypes = pipe(
  values,
  filterUnselectedServices,
  uniqServicesById,
  map(prop('id')),
  filter(Boolean),
)

export function extractGenericServices(services: any) {
  return [
    ServiceType.GenericServicePerBooking,
    ServiceType.GenericServicePerParticipant,
    ServiceType.GenericServicePerRoom,
  ].reduce((acc, key) => {
    const bucket = services?.[key]
    if (!bucket)
      return acc

    // bucket is an object like { REBOOKING: {...}, TRANSFER: {...}, ... }
    Object.assign(acc, bucket)

    return acc
  }, {} as Record<string, any>)
}

export function useUnselectedServices() {
  const { isLoading, isFetching, data } = useBookingDraftQuery()

  const rootServices = data?.services ?? {}
  const genericServices = extractGenericServices(rootServices)

  const unselectedServiceTypes = getUnselectedServiceTypes({
    ...rootServices,
    ...genericServices,
  })

  return {
    isLoading,
    isFetching,
    unselectedServiceTypes,
  }
}

export function useSetParticipantsBadge() {
  const queryClient = useQueryClient()
  const sid = useCheckoutSid()
  const { contextItemId } = useCheckoutContext()
  const queryKey = useBookingDraftQueryKey()

  const { isLoading, isFetching, data } = useBookingDraftQuery()

  const setParticipantsBadgeSelection = useMutation({
    mutationFn: async (participantsBadge: { id: string, name: string, birthdate?: string | Date, email?: string }[]) => {
      const payload = participantsBadge.map(({ id, name, birthdate, email }) => {
        return {
          id,
          name,
          ...(birthdate && { birthdate: formatBirthdateForPayload(birthdate) }),
          ...(email && { email }),
        }
      })
      await checkoutAPIClient.setParticipantsInformation({ sid, contextItemId }, payload)
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey })
    },
  })

  return {
    isLoading,
    isFetching,
    setParticipantsBadge: setParticipantsBadgeSelection.mutate,
    participants: data?.participants ?? null,
    rooms: data?.rooms ?? null,
    ageRestrictions: data?.configurations?.ageRestrictions ?? null,
    warning: data?.participantBadgeWarning ?? null,
  }
}

export function useCouponCode() {
  const queryClient = useQueryClient()
  const sid = useCheckoutSid()
  const { contextItemId } = useCheckoutContext()
  const queryKey = useBookingDraftQueryKey()

  const setCouponInfo = useCouponStore(state => state.setCouponInfo)

  const setCouponCode = useMutation({
    mutationFn: async (couponCode: string) => {
      const response = await checkoutAPIClient.setCouponCode({ sid, contextItemId, couponCode })
      if (response?.couponCodeSuccess) {
        setCouponInfo({
          value: couponCode,
          success: response?.couponCodeSuccess,
        })
      }
      else {
        setCouponInfo({
          value: couponCode,
          success: false,
        })
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey })
    },
  })

  return {
    setCouponCode: setCouponCode.mutate,
  }
}

export function useEngineErrorContactInfo() {
  const queryClient = useQueryClient()
  const sid = useCheckoutSid()
  const { contextItemId } = useCheckoutContext()
  const queryKey = useBookingDraftQueryKey()

  const setBookingInfoError = useEngineErrorContactInfoStore(state => state.setEngineErrorContactInfoResponse)

  const sendEngineErrorContactInfo = useMutation({
    mutationFn: async (info: { customerName: string, customerEmail?: string, customerPhone?: string }) => {
      const success = await checkoutAPIClient.sendEngineErrorContactInfo({
        sid,
        contextItemId,
        customerName: info.customerName,
        customerEmail: info.customerEmail,
        customerPhone: info.customerPhone,
      })
      setBookingInfoError(success)
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey })
    },
  })

  return {
    sendEngineErrorContactInfo: sendEngineErrorContactInfo.mutate,
  }
}

export function useNextStepNotification() {
  const sid = useCheckoutSid()
  const { contextItemId } = useCheckoutContext()
  const cookieConsent = useCookieConsent()

  const nextStepNotification = useMutation({
    mutationFn: async (stepNumber: number) => {
      await checkoutAPIClient.notifyNextStep({ sid, contextItemId, stepNumber, cookieConsent })
    },
    onError: (error) => {
      return error
    },
  })

  return {
    notifyNextStep: nextStepNotification.mutate,
  }
}

export function useBookingDraftQuery() {
  const sid = useCheckoutSid()
  const debug = useCheckoutDebugParameter()
  const testdata = useCheckoutTestDataParameter()
  const synthetic = useCheckoutSyntheticParameter()
  const { contextItemId } = useCheckoutContext()
  const queryKey = useBookingDraftQueryKey()
  let enableQuery = false
  if (sid) {
    enableQuery = true
  }

  const items = useCMSPageField('Checkout.items')?.items ?? []

  const enableBookingDraftQuery = enableQuery && Boolean(items)

  const bookingDraftQuery = useQuery<BookingDraft>({
    queryKey,
    queryFn: async () => {
      return checkoutAPIClient.getBookingDraft(
        {
          sid,
          contextItemId,
          ...(debug && { debug }),
          ...(testdata && { testdata }),
          ...(synthetic && { synthetic }),
        },
        {
          configData: items ?? [],
        },
      )
    },
    enabled: enableBookingDraftQuery,
    refetchOnWindowFocus: true,
    refetchOnMount: false,
    retry: false,
  })

  return {
    ...bookingDraftQuery,
  }
}

export function useBookingDraftQueryKey() {
  const sid = useCheckoutSid()

  return React.useMemo(() => ['booking-draft', sid], [sid])
}

export function useGetStepsTabs() {
  const sid = useCheckoutSid()
  const { contextItemId } = useCheckoutContext()

  let enableQuery = false
  if (sid) {
    enableQuery = true
  }

  const { isLoading, isFetching, data } = useQuery<StepsTabs>({
    queryKey: ['booking-steps-tabs', sid],
    queryFn: async () =>
      checkoutAPIClient.getStepsTabs({
        sid,
        contextItemId,
      }),
    enabled: enableQuery,
    retry: false,
    refetchOnWindowFocus: true,
    refetchOnMount: false,
  })

  return {
    isLoading,
    isFetching,
    activeStep: data?.steps.activeStep ?? '',
    items: data?.steps.items ?? [],
  }
}

export function useRyanairSessionQueryKey() {
  const sid = useCheckoutSid()

  return React.useMemo(() => ['ryanair-session-data', sid], [sid])
}

export function useRyanairSessionDataQuery() {
  const sid = useCheckoutSid()
  const debug = useCheckoutDebugParameter()
  const testdata = useCheckoutTestDataParameter()
  const { contextItemId } = useCheckoutContext()
  const queryKey = useRyanairSessionQueryKey()

  let enableQuery = false
  if (sid) {
    enableQuery = true
  }

  const { isLoading, isFetching, data, error } = useQuery<RyanairSessionData>({
    queryKey,
    queryFn: async () => {
      return checkoutAPIClient.getRyanairSessionData({
        sid,
        contextItemId,
        ...(debug && { debug }),
        ...(testdata && { testdata }),
      })
    },
    enabled: enableQuery,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false,
  })

  return {
    isLoading,
    isFetching,
    data,
    error,
  }
}
export function useInvalidateRyanairSessionData() {
  const queryClient = useQueryClient()
  const queryKey = useRyanairSessionQueryKey()

  const invalidateSession = React.useCallback(() => {
    queryClient.invalidateQueries({ queryKey })
  }, [queryClient, queryKey])

  return {
    invalidateSession,
  }
}

export function useCheckoutSidSession() {
  const isClient = typeof window !== 'undefined'
  const bookingNumber = useBookingNumber()
  const { draftId, setDraftId: setDraftIdState } = useCheckoutContext()
  const [hasChecked, setHasChecked] = React.useState(false)

  React.useEffect(() => {
    if (!bookingNumber) {
      setHasChecked(true)
      return
    }

    if (!isClient) {
      setHasChecked(true)
      return
    }

    const stored = sessionStorage.getItem('draftId')
    const storedBookingNumber = sessionStorage.getItem('draftId_bookingNumber')
    const isConfirmed = sessionStorage.getItem('draftId_isConfirmed') === 'true'

    if (stored && isConfirmed) {
      // Previous booking was confirmed — clear session so a fresh transform is triggered on Step1
      sessionStorage.removeItem('draftId')
      sessionStorage.removeItem('draftId_bookingNumber')
      sessionStorage.removeItem('draftId_isConfirmed')
      setDraftIdState && setDraftIdState('')
    }
    else if (stored && storedBookingNumber === bookingNumber) {
      if (setDraftIdState)
        setDraftIdState(stored)
    }
    else if (stored && storedBookingNumber !== bookingNumber) {
      // Booking number changed — clear stale session so a fresh transform is triggered
      sessionStorage.removeItem('draftId')
      sessionStorage.removeItem('draftId_bookingNumber')
      setDraftIdState && setDraftIdState('')
    }

    setHasChecked(true)
  }, [draftId, setDraftIdState, isClient, bookingNumber])

  const setDraftId = React.useCallback(
    (id: string) => {
      if (!bookingNumber)
        return

      if (!isClient)
        return
      sessionStorage.setItem('draftId', id)
      sessionStorage.setItem('draftId_bookingNumber', bookingNumber)
      setDraftIdState && setDraftIdState(id)
    },
    [setDraftIdState, isClient, bookingNumber],
  )

  const clearDraftId = React.useCallback(() => {
    if (!bookingNumber)
      return

    if (!isClient)
      return
    sessionStorage.removeItem('draftId')
    sessionStorage.removeItem('draftId_bookingNumber')
    sessionStorage.removeItem('draftId_isConfirmed')
    setDraftIdState && setDraftIdState('')
  }, [setDraftIdState, isClient, bookingNumber])

  return { draftId, setDraftId, clearDraftId, hasChecked }
}
export function useRouterIsReady() {
  const router = useRouter()
  const [isReady, setIsReady] = React.useState(false)

  React.useEffect(() => {
    if (router.isReady) {
      setIsReady(true)
    }
  }, [router.isReady])

  return isReady
}

export function useCheckoutSid() {
  const searchParams = useSearchParams()
  const draftIdFromParams = searchParams?.get('sid')

  const { draftId: draftIdFromSession } = useCheckoutSidSession()

  return React.useMemo(() => draftIdFromParams ?? draftIdFromSession ?? '', [draftIdFromParams, draftIdFromSession])
}

export function useCheckoutDebugParameter() {
  const searchParams = useSearchParams()
  return React.useMemo(() => searchParams?.get('debug') === 'true', [searchParams])
}
export function useCheckoutTestDataParameter() {
  const searchParams = useSearchParams()
  return React.useMemo(() => searchParams?.get('testdata') === 'true', [searchParams])
}
export function useCheckoutSyntheticParameter() {
  const searchParams = useSearchParams()
  return React.useMemo(
    () => searchParams?.get('synthetic') === 'true' || searchParams?.get('synthetic') === '1',
    [searchParams],
  )
}

function formatBirthdateForPayload(value: string | Date): string {
  if (value instanceof Date) {
    return formatDateToYYYMMDD(value)
  }
  return value
}
